import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/trpc";
import { UsageBillingEventDetailSheet } from "./usage-billing-event-detail-sheet";
import { UsageBillingEventRowCells } from "./usage-billing-event-row";
import { formatUsageBillingMonthLabel } from "./usage-billing-time";
import {
  getUsageBillingEventRowClassName,
  type UsageBillingEvent,
} from "./usage-billing-format";

const EVENTS_PREVIEW_LIMIT = 10;

type UsageBillingUnitEventsProps = {
  organizationBusinessUnitId: string;
  monthKey: string;
};

export function UsageBillingUnitEvents({
  organizationBusinessUnitId,
  monthKey,
}: UsageBillingUnitEventsProps) {
  const [selectedEvent, setSelectedEvent] = useState<UsageBillingEvent | null>(
    null,
  );
  const { data, isLoading } = useQuery(
    trpc.adminUsageBilling.getUnitEvents.queryOptions({
      organizationBusinessUnitId,
      month: monthKey,
      limit: EVENTS_PREVIEW_LIMIT,
    }),
  );

  const events = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-4 w-full" />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <p className="p-4 text-center text-sm text-gray-500">
        No calendar-month events recorded in{" "}
        {formatUsageBillingMonthLabel(monthKey)}.
      </p>
    );
  }

  const viewAllLabel =
    totalCount > EVENTS_PREVIEW_LIMIT
      ? `View all ${totalCount} events →`
      : "View full list →";

  return (
    <div className="p-4">
      <h3 className="mb-1 text-sm font-semibold">
        Calendar-month event ledger · {formatUsageBillingMonthLabel(monthKey)}
      </h3>
      <p className="mb-3 text-xs text-muted-foreground">
        Events in this calendar month can belong to cycles that started in
        another month.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Occurred at</TableHead>
            <TableHead>Category</TableHead>
            <TableHead className="text-right">Quantity</TableHead>
            <TableHead className="text-right">Basis amount</TableHead>
            <TableHead>Source</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <TableRow
              key={event.id}
              className={getUsageBillingEventRowClassName(event)}
              onClick={() => setSelectedEvent(event)}
            >
              <UsageBillingEventRowCells event={event} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {totalCount > 0 && (
        <div className="flex justify-center py-3">
          <Link
            to="/admin/usage-billing/$organizationBusinessUnitId"
            params={{ organizationBusinessUnitId }}
            search={{ month: monthKey }}
            className="text-sm text-blue-600 hover:underline"
          >
            {viewAllLabel}
          </Link>
        </div>
      )}
      <UsageBillingEventDetailSheet
        event={selectedEvent}
        onOpenChange={(open) => !open && setSelectedEvent(null)}
      />
    </div>
  );
}
